import React from 'react';
import { getCaraTriggerEmailAddress } from '@oysterjs/core/email';
import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@oysterjs/ui/dialog';

import {
  GetTriggerDefinitionsDocument,
  TriggerDefinitionStatus,
  type TriggerDefinitionFieldsFragment,
  useArchiveTriggerDefinitionMutation,
  usePauseTriggerDefinitionMutation,
  useResumeTriggerDefinitionMutation
} from '~/api/graphql';
import { FormError } from '~/components/form-error';

const DELETE_FAILED = 'Could not delete this trigger. Please try again.';
const PAUSE_FAILED = 'Could not pause this trigger. Please try again.';
const RESUME_FAILED = 'Could not resume this trigger. Please try again.';

/** Runs a mutation and returns the message to show, or null when it succeeded. */
const runMutation = async (
  mutate: () => Promise<{ errors?: readonly unknown[] | null }>,
  failure: string
): Promise<string | null> => {
  try {
    return messageOfFailure(await mutate(), failure);
  } catch (error) {
    return messageFromError(error, failure);
  }
};

/**
 * Pause, resume, and delete, each resolving to an error message or null. The
 * list refetch is awaited so a caller that revalidates or navigates next reads
 * the refreshed list.
 */
export const useTriggerMutations = () => {
  const options = {
    refetchQueries: [{ query: GetTriggerDefinitionsDocument }],
    awaitRefetchQueries: true
  };
  const [pause, pauseState] = usePauseTriggerDefinitionMutation(options);
  const [resume, resumeState] = useResumeTriggerDefinitionMutation(options);
  const [archive, archiveState] = useArchiveTriggerDefinitionMutation(options);

  const toggleStatus = (trigger: TriggerDefinitionFieldsFragment) => {
    const variables = { id: trigger.id, version: trigger.version };
    return trigger.status === TriggerDefinitionStatus.Active
      ? runMutation(() => pause({ variables }), PAUSE_FAILED)
      : runMutation(() => resume({ variables }), RESUME_FAILED);
  };

  const remove = (trigger: TriggerDefinitionFieldsFragment) =>
    runMutation(() => archive({ variables: { id: trigger.id } }), DELETE_FAILED);

  return {
    toggleStatus,
    remove,
    pausing: pauseState.loading,
    resuming: resumeState.loading,
    deleting: archiveState.loading
  };
};

/** Confirms a delete, and stays open with the reason when the server refuses it. */
export const DeleteTriggerDialog = ({
  trigger,
  emailNamespace,
  onClose,
  onDeleted
}: {
  trigger: TriggerDefinitionFieldsFragment | null;
  emailNamespace: string;
  onClose: () => void;
  onDeleted: () => void;
}) => {
  const { remove, deleting } = useTriggerMutations();
  const [error, setError] = React.useState<string | null>(null);
  const emailSuffix = trigger?.eventMatcher.caraAddressConfig?.emailSuffix;

  const close = () => {
    setError(null);
    onClose();
  };

  const confirm = async () => {
    if (!trigger) return;
    setError(null);
    const message = await remove(trigger);
    if (message) {
      setError(message);
      return;
    }
    onDeleted();
  };

  return (
    <Dialog open={!!trigger} onClose={close}>
      <DialogTitle>Delete trigger</DialogTitle>
      <DialogDescription>
        {trigger ? (
          <>
            Are you sure you want to delete <strong>{trigger.name}</strong>? It will stop running.
            {emailSuffix && emailNamespace ? (
              <>
                {' '}
                Another trigger can then use{' '}
                <span className="font-mono">
                  {getCaraTriggerEmailAddress(emailNamespace, emailSuffix)}
                </span>
                .
              </>
            ) : null}
          </>
        ) : null}
      </DialogDescription>
      <FormError message={error} className="mt-4 text-sm text-red-600 dark:text-red-400" />
      <DialogActions>
        <Button color="white" onClick={close}>
          Cancel
        </Button>
        <Button color="sky" loading={deleting} onClick={() => void confirm()}>
          Delete trigger
        </Button>
      </DialogActions>
    </Dialog>
  );
};
